import React, { useState, useRef, useMemo } from 'react';
import { getPaginationModel } from './paginationUtils';
import RecipeTooltip from './RecipeTooltip';

const RecipeList = ({ recipes, selectedRecipes, onRecipeSelection, onEdit, onDelete, onUnselectAll, cookbooks, categories, ingredients, page, onPageChange, searchTerm, selectedCookbookIds, selectedCategoryIds, selectedIngredientIds, onRemoveCookbook, onRemoveCategory, onRemoveIngredient, isShuffled }) => {
  const [tooltip, setTooltip] = useState({ visible: false, recipe: null, target: null, triggeredBy: null });
  const tooltipTimer = useRef(null);

  const handleMouseEnter = (e, recipe) => {
    if (window.innerWidth < 768) return;
    const target = e.currentTarget;
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    tooltipTimer.current = setTimeout(() => {
      setTooltip({
        visible: true,
        recipe,
        target,
        triggeredBy: 'hover'
      });
    }, 500); // 500ms delay
  };

  const handleMouseLeave = () => {
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    if (tooltip.triggeredBy === 'hover') {
      setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null });
    }
  };

  const sortedRecipes = useMemo(() => {
    let result = [...recipes];
    if (isShuffled) {
      // Fisher-Yates shuffle
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
      }
    } else {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }
    return result;
  }, [recipes, isShuffled]);

  const pageSize = 5;
  const totalPages = Math.ceil(sortedRecipes.length / pageSize);
  const paginatedRecipes = sortedRecipes.slice((page - 1) * pageSize, page * pageSize);
  const paginationModel = getPaginationModel(page, totalPages);

  const getCookbookTitle = (cookbookId) => {
    const cookbook = cookbooks.find(c => c.id === cookbookId);
    return cookbook ? cookbook.title : '';
  };

  const getCategoryTitles = (categoryIds) => {
    if (!categoryIds) return '';
    return categoryIds.map(id => {
      const category = categories.find(c => c.id === id);
      return category ? category.title : '';
    }).join(', ');
  };

  const getCookbookTitles = (cookbookIds) => {
    if (!cookbookIds) return '';
    return cookbookIds.map(id => {
      const cookbook = cookbooks.find(c => c.id === id);
      return cookbook ? cookbook.title : '';
    }).join(', ');
  };

  const getIngredientNames = (ingredientIds) => {
    if (!ingredientIds || !ingredients) return '';
    return ingredientIds.map(id => {
      const ingredient = ingredients.find(i => i.id === id);
      return ingredient ? ingredient.name : '';
    }).join(', ');
  };

  const getRecipeDetails = (recipe) => {
    const cookbookTitle = getCookbookTitle(recipe.cookbookId);
    const pageNumber = recipe.pageNumber;
    const categoriesString = recipe.categoryIds && recipe.categoryIds.map(id => categories.find(c => c.id === id)?.title).join(', ');

    let details = '';
    if (cookbookTitle) {
      details += cookbookTitle;
    }
    if (pageNumber) {
      details += ` p.${pageNumber}`;
    }
    if (categoriesString && (cookbookTitle || pageNumber)) {
      details += ' - ';
    }
    details += categoriesString;
    return details;
  };

  const getOperationColor = (operation) => {
    switch (operation) {
      case 'AND': return '#4CAF50';
      case 'OR': return '#2196F3';
      case 'NOT': return '#f44336';
      default: return '#ccc';
    }
  };

  const emptyRows = pageSize - paginatedRecipes.length;

  const filterChips = (
    <div className="filter-chips-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px', padding: '0 1rem' }}>
      {selectedCookbookIds.map(item => {
        const id = typeof item === 'object' && item !== null ? item.id : item;
        const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
        const cookbook = cookbooks.find(c => c.id === id);
        if (!cookbook) return null;
        return (
          <div key={id} className="selected-item" style={{ backgroundColor: getOperationColor(operation) }}>
            <i className="material-icons" style={{ fontSize: '16px', marginRight: '5px' }}>menu_book</i>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
            {cookbook.title}
            <button title="Remove cookbook" onClick={() => onRemoveCookbook(id)} className="remove-item-button" style={{ background: 'none', border: 'none', color: 'white', marginLeft: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ fontSize: '16px' }}>close</i></button>
          </div>
        );
      })}
      {selectedCategoryIds.map(item => {
        const id = typeof item === 'object' && item !== null ? item.id : item;
        const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
        const category = categories.find(c => c.id === id);
        if (!category) return null;
        return (
          <div key={id} className="selected-item" style={{ backgroundColor: getOperationColor(operation) }}>
            <i className="material-icons" style={{ fontSize: '16px', marginRight: '5px' }}>widgets</i>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
            {category.title}
            <button title="Remove category" onClick={() => onRemoveCategory(id)} className="remove-item-button" style={{ background: 'none', border: 'none', color: 'white', marginLeft: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ fontSize: '16px' }}>close</i></button>
          </div>
        );
      })}
      {selectedIngredientIds.map(item => {
        const id = typeof item === 'object' && item !== null ? item.id : item;
        const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
        const ingredient = ingredients.find(i => i.id === id);
        if (!ingredient) return null;
        return (
          <div key={id} className="selected-item" style={{ backgroundColor: getOperationColor(operation) }}>
            <i className="material-icons" style={{ fontSize: '16px', marginRight: '5px' }}>kitchen</i>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
            {ingredient.name}
            <button title="Remove ingredient" onClick={() => onRemoveIngredient(id)} className="remove-item-button" style={{ background: 'none', border: 'none', color: 'white', marginLeft: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ fontSize: '16px' }}>close</i></button>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {filterChips}
      {paginatedRecipes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No recipes found.</div>
      ) : (
        <>
          <ul className="recipe-list" data-tour="recipe-list">
            {paginatedRecipes.map(recipe => {
              const selectionCount = selectedRecipes.filter(id => id === recipe.id).length;
              return (
                <li
                  key={recipe.id}
                  data-tour={recipe.isTemporary ? `recipe-${recipe.id}` : null}
                  className={`recipe-item ${selectionCount > 0 ? 'selected' : ''}`}
                  style={{ position: 'relative', borderLeft: selectionCount > 0 ? 'none' : undefined }}
                  onClick={() => onRecipeSelection(recipe)}
                  onMouseEnter={(e) => handleMouseEnter(e, recipe)}
                  onMouseLeave={handleMouseLeave}
                >
                  {selectionCount > 0 && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      {[...Array(selectionCount)].map((_, i) => (
                        <div key={i} style={{ flex: 1, backgroundColor: '#2196F3' }}></div>
                      ))}
                    </div>
                  )}
                  <div className="recipe-info" style={{ paddingLeft: selectionCount > 0 ? '6px' : '0' }}>
                    <div className="recipe-name">
                      <button
                        className="icon-button mobile-view-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tooltip.visible && tooltip.recipe && tooltip.recipe.id === recipe.id && tooltip.triggeredBy === 'click') {
                            setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null });
                          } else {
                            setTooltip({ visible: true, recipe, target: e.currentTarget, triggeredBy: 'click' });
                          }
                        }}
                        title="View details"
                      >
                        <i className="material-icons">visibility</i>
                      </button>
                      <span>{recipe.name}</span>
                    </div>
                    <span className="recipe-details">{getRecipeDetails(recipe)}</span>
                  </div>
                  <div className="recipe-actions">
                    <button onClick={(e) => { e.stopPropagation(); onEdit(recipe); }} className="icon-button" title="Edit recipe"><i className="material-icons">edit</i></button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(recipe.id); }} className="icon-button delete-button" title="Delete recipe"><i className="material-icons">delete</i></button>
                  </div>
                </li>
              );
            })}
            {page === totalPages && totalPages > 1 && emptyRows > 0 && [...Array(emptyRows)].map((_, index) => (
              <li key={`empty-${index}`} className="recipe-item" style={{ visibility: 'hidden' }}>
                &nbsp;
                <div className="recipe-actions empty-row-actions">
                  <button className="icon-button" title="Edit recipe"><i className="material-icons">edit</i></button>
                  <button className="icon-button delete-button" title="Delete recipe"><i className="material-icons">delete</i></button>
                </div>
              </li>
            ))}
          </ul>
          {tooltip.visible && (
            <RecipeTooltip
              recipe={tooltip.recipe}
              target={tooltip.target}
              cookbooks={cookbooks}
              mobileMode={tooltip.triggeredBy === 'click'}
              onClose={() => setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null })}
            />
          )}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="icon-button" title="Previous page"><i className="material-icons">keyboard_arrow_left</i></button>
              <div className="page-numbers">
                {paginationModel.map((item, index) => (
                  <button key={index} onClick={() => onPageChange(item)} disabled={item === '...' || item === page} className={item === page ? 'active' : ''} title={`Go to page ${item}`}>
                    {item}
                  </button>
                ))}
              </div>
              <button onClick={() => onPageChange(page + 1)} disabled={paginatedRecipes.length < pageSize || page * pageSize >= recipes.length} className="icon-button" title="Next page"><i className="material-icons">keyboard_arrow_right</i></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecipeList;